<template>
  <app-collapse class="mt-1">
    <app-collapse-item
      :is-visible="false"
      class="collapsible-section-bg"
      title="Importer information"
    >
      <template #header>
        <span
          class="lead collapse-title"
        >
          <section-status-icon
            :has-data="hasImporterData"
            :has-errors="hasSectionErrors"
          />
          Importer information
        </span>
      </template>
      <validation-observer>
        <!-- Importer Section -->
        <b-row v-if="parcel.importer">
          <b-col
            lg="3"
            md="4"
          >
            <validation-provider
              v-slot="{ errors }"
              name="Importer's identification number"
              rules=""
            >
              <label>
                Importer's identification number
                <small class="text-muted">1304017000</small>
                <feather-icon
                  v-b-tooltip.hover.top="'Importer identification number (EORI, VAT, etc.)'"
                  icon="InfoIcon"
                  size="14"
                  class="ml-50 text-muted"
                />
              </label>
              <b-form-input
                v-model="parcel.importer.registry_number"
                v-b-tooltip.hover.top="getIssueMessage('importer.registry_number')"
                :class="getIssueClass('importer.registry_number')"
                :disabled="disabled"
                class="mr-0 ml-0"
                :state="errors.length > 0 ? false : null"
              />
            </validation-provider>
          </b-col>

          <b-col
            lg="3"
            md="4"
          >
            <validation-provider
              v-slot="{ errors }"
              name="Importer's name"
              rules=""
            >
              <label>
                Importer's name
                <small class="text-muted">1304016000</small>
                <feather-icon
                  v-b-tooltip.hover.top="'Full name or company name of the importer'"
                  icon="InfoIcon"
                  size="14"
                  class="ml-50 text-muted"
                />
              </label>
              <b-form-input
                v-model="parcel.importer.name"
                v-b-tooltip.hover.top="getIssueMessage('importer.name')"
                :class="getIssueClass('importer.name')"
                :disabled="disabled"
                class="mr-0 ml-0"
                :state="errors.length > 0 ? false : null"
              />
            </validation-provider>
          </b-col>

          <b-col
            lg="3"
            md="4"
          >
            <validation-provider
              v-slot="{ errors }"
              name="Street and number"
              rules=""
            >
              <label>
                Street and number
                <small class="text-muted">1304018019</small>
              </label>
              <b-form-input
                v-model="parcel.importer.street_and_number"
                v-b-tooltip.hover.top="getIssueMessage('importer.street_and_number')"
                :class="getIssueClass('importer.street_and_number')"
                :disabled="disabled"
                class="mr-0 ml-0"
                :state="errors.length > 0 ? false : null"
              />
            </validation-provider>
          </b-col>

          <b-col
            lg="3"
            md="4"
          >
            <validation-provider
              v-slot="{ errors }"
              name="Postal code"
              rules="max:20"
            >
              <label>
                Postal code
                <small class="text-muted">1304018021</small>
              </label>
              <b-form-input
                v-model="parcel.importer.postal_code"
                v-b-tooltip.hover.top="getIssueMessage('importer.postal_code')"
                :class="getIssueClass('importer.postal_code')"
                :disabled="disabled"
                class="mr-0 ml-0"
                :state="errors.length > 0 ? false : null"
              />
            </validation-provider>
          </b-col>

          <b-col
            lg="3"
            md="4"
          >
            <validation-provider
              v-slot="{ errors }"
              name="City"
              rules=""
            >
              <label>
                City
              </label>
              <b-form-input
                v-model="parcel.importer.city"
                v-b-tooltip.hover.top="getIssueMessage('importer.city')"
                :class="getIssueClass('importer.city')"
                :disabled="disabled"
                class="mr-0 ml-0"
                :state="errors.length > 0 ? false : null"
              />
            </validation-provider>
          </b-col>

          <b-col
            lg="3"
            md="4"
          >
            <validation-provider
              v-slot="{ errors }"
              name="Country"
              rules=""
            >
              <label>
                Country
                <small class="text-muted">1304018020</small>
              </label>
              <classifier-select
                v-model="parcel.importer.country"
                :classifier-key="classifierKeys.countries"
                :disabled="disabled"
                :state="errors.length > 0 ? false : null"
              />
            </validation-provider>
          </b-col>
        </b-row>
      </validation-observer>
    </app-collapse-item>
  </app-collapse>
</template>

<script>
import {
  BRow,
  BCol,
  BFormInput,
  VBTooltip,
} from 'bootstrap-vue'
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import AppCollapse from '@core/components/app-collapse/AppCollapse.vue'
import AppCollapseItem from '@core/components/app-collapse/AppCollapseItem.vue'
import errorHighlightingMixin from '@/mixins/errorHighlightingMixin'
import SectionStatusIcon from '@/views/shipments/declarations/modals/components/h1/SectionStatusIcon.vue'
import ClassifierSelect from '@/views/components/ClassifierSelect.vue'
import { CUSTOMS_CLASSIFIER_KEYS } from '@/libs/customsClassifiers'

export default {
  components: {
    BRow,
    BCol,
    BFormInput,
    ValidationObserver,
    ValidationProvider,
    AppCollapse,
    AppCollapseItem,
    SectionStatusIcon,
    ClassifierSelect,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  mixins: [errorHighlightingMixin],
  props: {
    parcel: {
      type: Object,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return { classifierKeys: CUSTOMS_CLASSIFIER_KEYS }
  },
  computed: {
    hasImporterData() {
      if (!this.parcel || !this.parcel.importer) {
        return false
      }
      const {
        registry_number: registryNumber,
        name,
        country,
        street_and_number: streetAndNumber,
        city,
        postal_code: postalCode,
      } = this.parcel.importer
      return !!(
        registryNumber
        || name
        || country
        || streetAndNumber
        || city
        || postalCode
      )
    },
    hasSectionErrors() {
      return Object.keys(this.issuesMap).some(key => key.startsWith('importer.'))
    },
  },
}
</script>

<style scoped lang="scss">
@import '@core/scss/base/bootstrap-extended/variables';
@import '@core/scss/base/core/mixins/declarations-mixins';

.collapsible-section-bg {
  @include collapsible-section-bg;
}

.input-warning {
  @include input-warning;
}

.input-danger {
  @include input-danger;
}
</style>
